<template>
  <!-- 
    瀑布流根容器 
    - position: relative; 用于子元素 absolute 定位的基础
    - height: 动态计算，因为子元素脱离文档流，必须手动撑开父容器高度
  -->
  <div 
    ref="containerRef" 
    class="common-waterfall-root" 
    :style="{ height: containerHeight + 'px' }"
  >
    <!-- 
      使用 transition-group 处理卡片进入时的动画 
      name="waterfall-anim" 对应底部的 CSS 动画
    -->
    <transition-group name="waterfall-anim">
      <div
        v-for="(item, index) in positionedItems"
        :key="item.id"
        class="waterfall-item-wrapper"
        :style="item.style"
        :data-id="item.id"
      >
        <!-- 
          作用域插槽 (Scoped Slot):
          这是组件通用的核心。它不关心卡片长什么样，而是把布局信息传回给调用者。
          - item: 原始数据对象
          - index: 索引
          - columnWidth: 当前计算出的动态列宽
          - isWaterfall: 告知外部当前是否为瀑布流模式，方便外部调整图片样式（如 object-fit）
        -->
        <slot 
          name="item" 
          :item="item.raw" 
          :index="index" 
          :columnWidth="columnWidth"
          :isWaterfall="isWaterfall"
        ></slot>
      </div>
    </transition-group>
  </div>
</template>

<script setup lang="ts">
/**
 * 【通用瀑布流组件 - 学习手册】
 * 
 * 核心原理：
 * 1. 动态列宽：根据容器总宽度和“基准宽度(baseWidth)”，计算出当前视口最适合排几列。
 * 2. 贪心算法布局：
 *    - 在瀑布流模式下，每次放置新卡片时，都会寻找当前高度最短的那一列。
 *    - 在网格模式下，按照 0, 1, 2... 的顺序循环填充每一列。
 * 3. 实时高度获取：
 *    - 难点：网络图片加载前没有高度，或者文字内容是动态生成的。
 *    - 方案：使用 Web API "ResizeObserver" 实时监听 DOM 节点的高度变化。只要内容撑开了卡片，就立即通知布局刷新。
 * 4. 节流优化：
 *    - 频繁的布局计算（如滚动、内容变化）会消耗 CPU。通过 setTimeout 限制计算频率。
 */

import { ref, onMounted, onUnmounted, watch, nextTick } from 'vue';

interface Props {
  items: any[];           // 数据列表
  isWaterfall?: boolean;  // 模式开关：true=瀑布流，false=等高网格排齐
  rowKey?: string;        // 数据的唯一 ID 字段
  minWidth?: number;      // 单列最小宽度（防止在小屏上卡片太挤）
  maxWidth?: number;      // 单列最大宽度（防止在大屏上卡片太宽）
  baseWidth?: number;     // 基准列宽（计算列数的主要依据，移动端通常设置 160-180 容易出 2 列）
  gap?: number;           // 卡片之间的间距（水平和垂直同步）
  fixedHeight?: number;   // 网格模式下，期望的卡片固定高度（包含文字内容的总高度）
}

const props = withDefaults(defineProps<Props>(), {
  items: () => [],
  isWaterfall: true,
  rowKey: 'id',
  minWidth: 140,
  maxWidth: 300,
  baseWidth: 170, 
  gap: 12,
  fixedHeight: 245 
});

// --- 状态存储 ---
const containerRef = ref<HTMLElement | null>(null);
const columnWidth = ref(0);
const columns = ref(2);
const containerHeight = ref(0);
const positionedItems = ref<any[]>([]);

// 存储所有正在监听高度的 Observer，方便销毁
const itemObservers = new Map<Element, ResizeObserver>();

// 持久化存储每个 ID 对应的高度，防止切换 tab 或重新计算时高度抖动
const measuredHeightsMap = reactive(new Map<string | number, number>());

// --- 逻辑 1：计算列数和列宽 ---
const calculateColumnsAndWidth = () => {
  if (!containerRef.value) return;
  const totalWidth = containerRef.value.offsetWidth;
  if (totalWidth <= 0) return;

  // 计算理想列数 = (总宽 + 间距) / (基准宽 + 间距)
  let cols = Math.floor((totalWidth + props.gap) / (props.baseWidth + props.gap));
  cols = Math.max(1, cols); 
  
  // 反推实际宽度
  let actualWidth = (totalWidth - (cols - 1) * props.gap) / cols;
  
  // 修正：如果算出来的宽度比 minWidth 还小，说明太挤了，减少一列
  if (actualWidth < props.minWidth && cols > 1) {
    cols -= 1;
    actualWidth = (totalWidth - (cols - 1) * props.gap) / cols;
  }
  
  // 修正：如果比 maxWidth 还大，说明太稀疏了，增加一列
  if (actualWidth > props.maxWidth) {
    cols += 1;
    actualWidth = (totalWidth - (cols - 1) * props.gap) / cols;
  }

  columns.value = cols;
  columnWidth.value = actualWidth;
};

// --- 逻辑 2：瀑布流/网格排版算法 ---
const performLayout = () => {
  // 记录每一列当前积累的总高度
  const colHeights = new Array(columns.value).fill(0);
  
  positionedItems.value = props.items.map((item, index) => {
    let colIndex = 0;
    
    if (props.isWaterfall) {
      // 【瀑布流算法】：每次找最矮的那一列插进去
      const minHeight = Math.min(...colHeights);
      colIndex = colHeights.indexOf(minHeight);
    } else {
      // 【标准网格模式】：按顺序排列
      colIndex = index % columns.value;
    }
    
    const left = colIndex * (columnWidth.value + props.gap);
    const top = colHeights[colIndex];
    
    /**
     * 高度处理：
     * 1. 优先从 measuredHeightsMap 获取测量过的真实高度。
     * 2. 否则：瀑布流模式基于 375:494 比例预估高度（媒体区 + 文字预留约 90px）。
     */
    const itemId = item[props.rowKey] || index;
    const measuredHeight = measuredHeightsMap.get(itemId);
    const itemHeight = measuredHeight || (props.isWaterfall ? (columnWidth.value * (494 / 375) + 90) : props.fixedHeight);

    // 更新该列高度（包含间距）
    colHeights[colIndex] += itemHeight + props.gap;
    
    return {
      raw: item,
      id: itemId,
      style: {
        position: 'absolute',
        left: `${left}px`,
        top: `${top}px`,
        width: `${columnWidth.value}px`,
        // 网格模式强制统一高度，瀑布流模式随内容自适应
        height: props.isWaterfall ? 'auto' : `${props.fixedHeight}px`,
      }
    };
  });
  
  // 撑开父容器：取所有列中最高的那一列
  containerHeight.value = Math.max(...colHeights);
};

// --- 逻辑 3：内容变化实时监测 (ResizeObserver) ---
const observeItem = (el: Element) => {
  if (itemObservers.has(el)) return;

  const observer = new ResizeObserver((entries) => {
    // 使用 requestAnimationFrame 避免 "ResizeObserver loop limit exceeded" 报错
    window.requestAnimationFrame(() => {
      const entry = entries[0];
      if (!entry) return;
      const height = entry.contentRect.height;
      
      const id = (el as HTMLElement).dataset.id;
      if (id && measuredHeightsMap.get(id) !== height) {
        measuredHeightsMap.set(id, height);
        throttledLayout();
      }
    });
  });

  observer.observe(el);
  itemObservers.set(el, observer);
};

// --- 性能优化：节流函数 (Throttling) ---
let layoutTimer: number | null = null;
const throttledLayout = () => {
  if (layoutTimer) return;
  layoutTimer = window.setTimeout(() => {
    performLayout();
    layoutTimer = null;
  }, 60); // 60ms 间隔，兼顾流畅度与性能
};

// --- 生命周期管理 ---
onMounted(() => {
  calculateColumnsAndWidth();
  performLayout();
  
  // 监听容器大小变化（如屏幕旋转、浏览器缩放）
  const containerObserver = new ResizeObserver(() => {
    calculateColumnsAndWidth();
    throttledLayout();
  });
  if (containerRef.value) containerObserver.observe(containerRef.value);

  onUnmounted(() => {
    containerObserver.disconnect();
    itemObservers.forEach(obs => obs.disconnect());
  });
});

// 核心监听：每当数据源 (items) 更新时（如分页加载），给新卡片绑定高度监测
watch(() => props.items, (newVal) => {
  nextTick(() => {
    const els = containerRef.value?.querySelectorAll('.waterfall-item-wrapper');
    els?.forEach((el) => observeItem(el));
    throttledLayout();
  });
}, { deep: true, immediate: true });

// 监听瀑布流/网格切换
watch(() => props.isWaterfall, () => {
  throttledLayout();
});

</script>

<style scoped>
.common-waterfall-root {
  position: relative;
  width: 100%;
  /* 容器高度平滑过渡 */
  transition: height 0.3s ease-out;
}

.waterfall-item-wrapper {
  /* 必须启用 absolute 定位 */
  will-change: transform;
  /* 平滑的位置移动动画：当窗口缩放或模式切换时，卡片会飞过去 */
  transition: left 0.3s cubic-bezier(0.25, 0.1, 0.25, 1), 
              top 0.3s cubic-bezier(0.25, 0.1, 0.25, 1), 
              width 0.3s cubic-bezier(0.25, 0.1, 0.25, 1);
}

/* --- 分页加载入场动画 (Vue transition-group) --- */
.waterfall-anim-enter-active {
  transition: all 0.4s ease-out;
}
.waterfall-anim-enter-from {
  opacity: 0;
  transform: translateY(40px) scale(0.95);
}
/* 防止离场时瞬间闪烁 */
.waterfall-anim-leave-active {
  position: absolute;
  opacity: 0;
}
</style>

